// src/portal-app/PortalLogin.js
//
// F12b Task 5 — manager sign-in for the native Aqana Manager app. Rendered
// by PortalAppShell whenever AuthContext reports unauthenticated; the first
// thing a manager ever sees in this binary.
//
// Flow: business short-ID + username + password → AuthContext.login(username,
// password, shortId). On success we also mark selectedPortal='manager' (the
// same flavour stamp the web LoginPage applies) and AuthContext flips
// isAuthenticated, so PortalAppShell re-renders straight into PortalShell —
// no navigation from here. login() throws on failure (axios error with
// response.data.detail) and could in principle resolve falsy; BOTH paths
// surface an inline role="alert" error and re-enable the submit button.
//
// Visual language: Liquid Glass (Task 3 primitives) — full-intensity aurora
// for this hero/auth moment, a veiled GlassCard for the form so body ink
// stays >= 4.5:1 over the drifting blobs. Every colour reads --c-* tokens;
// logical properties keep RTL mirroring free.
//
// UX rules applied (ui-ux-pro-max, forms/login):
//   - visible <label for> on every field (never placeholder-only labelling)
//   - per-field "required" errors validate on blur and sit BELOW the field
//   - auth failure error sits near the submit button with role="alert"
//   - >= 44pt touch targets (inputs 52px, toggle 44px, submit 54px)
//   - password show/hide toggle (aria-pressed + translated aria-label)
//   - loading state: submit disables and shows a spinner while signing in
//   - semantic input attributes (inputMode, autoComplete, autoCapitalize)
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LiquidGlassBackground, GlassCard } from './shell/LiquidGlass';

/* ------------------------------------------------------------------ */
/* Stylesheet — tokens only (--c-* flips light/dark automatically).    */
/* ------------------------------------------------------------------ */
const CSS = `
.plg-wrap{flex:1;display:flex;align-items:center;justify-content:center;overflow-y:auto;box-sizing:border-box;
  padding:calc(24px + var(--safe-top, 0px)) 20px calc(24px + var(--safe-bottom, 0px));}
.plg-card{width:100%;max-width:430px;box-sizing:border-box;padding:30px 28px 26px;animation:plgIn .35s ease;}
@keyframes plgIn{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
@keyframes plgSpin{to{transform:rotate(360deg);}}
@media (prefers-reduced-motion: reduce){.plg-card{animation:none !important;}}

.plg-brand{display:flex;align-items:center;justify-content:center;gap:11px;margin-bottom:22px;}
.plg-brandmark{width:42px;height:42px;border-radius:12px;flex:none;display:flex;align-items:center;justify-content:center;}
/* Real brand asset, not a drawn glyph. Dark ink on light surfaces, the white
   variant on dark ones — a dark mark on a dark card would vanish. Mirrors
   StationTopBar and the POS lock screen. */
.plg-brandmark img{width:100%;height:100%;object-fit:contain;display:block;}
:root[data-theme="dark"] .plg-brandmark img.mark-dark{display:none;}
.plg-brandmark img.mark-light{display:none;}
:root[data-theme="dark"] .plg-brandmark img.mark-light{display:block;}
.plg-wordmark{font-size:19px;font-weight:800;letter-spacing:-.3px;color:var(--c-ink);}

.plg-eyebrow{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1.4px;color:var(--c-greenDark);text-align:center;margin:0 0 6px;}
.plg-title{font-size:22px;font-weight:800;letter-spacing:-.4px;color:var(--c-ink);text-align:center;margin:0 0 6px;}
.plg-sub{font-size:13.5px;font-weight:600;color:var(--c-slate);text-align:center;line-height:1.55;margin:0 auto 22px;max-width:340px;}

.plg-field{margin-bottom:14px;}
.plg-label{display:block;font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;color:var(--c-slate);margin-bottom:7px;}
.plg-input{width:100%;min-height:52px;padding:0 16px;box-sizing:border-box;border-radius:14px;
  border:1.5px solid var(--c-border);background:var(--c-surface);color:var(--c-text);
  font-size:15px;font-weight:600;font-family:inherit;
  transition:border-color .15s ease, box-shadow .15s ease;}
.plg-input::placeholder{color:var(--c-mutedLight);font-weight:500;}
.plg-input:focus{outline:none;border-color:var(--c-green);box-shadow:0 0 0 3px var(--c-greenLight);}
.plg-input.plg-invalid{border-color:var(--c-red);}
.plg-input.plg-invalid:focus{border-color:var(--c-red);box-shadow:0 0 0 3px var(--c-redLight);}

.plg-pwd{position:relative;}
.plg-pwd .plg-input{padding-inline-end:56px;}
.plg-toggle{position:absolute;top:50%;transform:translateY(-50%);inset-inline-end:6px;
  width:44px;height:44px;display:flex;align-items:center;justify-content:center;
  border:none;background:transparent;border-radius:11px;cursor:pointer;color:var(--c-slate);
  transition:color .15s ease, background .15s ease;-webkit-tap-highlight-color:transparent;}
.plg-toggle:hover,.plg-toggle:active{color:var(--c-ink);background:var(--c-greenLight);}
.plg-toggle:focus-visible{outline:2px solid var(--c-green);outline-offset:1px;}

.plg-field-error{display:flex;align-items:center;gap:6px;margin:7px 2px 0;
  color:var(--c-redText);font-size:12px;font-weight:700;line-height:1.4;}
.plg-field-error svg{flex:none;}

.plg-error{display:flex;gap:10px;align-items:flex-start;padding:12px 14px;border-radius:13px;margin-bottom:14px;
  background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-redText);
  font-size:13px;font-weight:600;line-height:1.5;animation:plgIn .2s ease;}
.plg-error svg{flex:none;margin-top:1px;}

.plg-btn{width:100%;min-height:54px;margin-top:6px;border:none;border-radius:15px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:10px;
  background:linear-gradient(180deg,var(--c-green) 0%,var(--c-greenDark) 100%);
  color:#fff;font-size:16px;font-weight:800;letter-spacing:-.2px;font-family:inherit;
  box-shadow:0 6px 18px color-mix(in srgb, var(--c-green) 35%, transparent), inset 0 1px 0 rgba(255,255,255,.18);
  transition:transform .15s ease, filter .15s ease, opacity .15s ease;-webkit-tap-highlight-color:transparent;}
.plg-btn:active{transform:scale(.97);filter:brightness(1.08);}
.plg-btn:disabled{opacity:.55;cursor:default;transform:none;filter:none;}
.plg-btn:focus-visible{outline:2px solid var(--c-ink);outline-offset:2px;}
.plg-spin{animation:plgSpin .8s linear infinite;}
[dir="rtl"] .plg-btn svg.plg-arrow{transform:scaleX(-1);}

.plg-foot{margin-top:20px;text-align:center;font-size:11px;font-weight:800;letter-spacing:3px;text-transform:uppercase;color:var(--c-slate);opacity:.6;}

@media (min-width:768px){
  .plg-wrap{padding:calc(40px + var(--safe-top, 0px)) 40px calc(40px + var(--safe-bottom, 0px));}
  .plg-card{max-width:480px;padding:44px 48px 36px;}
  .plg-brand{gap:14px;margin-bottom:28px;}
  .plg-brandmark{width:54px;height:54px;border-radius:16px;}
  .plg-brandmark img{width:100%;height:100%;}
  .plg-wordmark{font-size:25px;}
  .plg-eyebrow{font-size:12px;letter-spacing:1.8px;margin-bottom:8px;}
  .plg-title{font-size:28px;margin-bottom:8px;}
  .plg-sub{font-size:15px;max-width:400px;margin-bottom:28px;}
  .plg-field{margin-bottom:17px;}
  .plg-label{font-size:12px;margin-bottom:8px;}
  .plg-input{min-height:56px;font-size:16px;padding:0 18px;border-radius:15px;}
  .plg-pwd .plg-input{padding-inline-end:58px;}
  .plg-btn{min-height:58px;font-size:17px;border-radius:16px;}
  .plg-error{font-size:14px;}
  .plg-foot{margin-top:26px;}
}
`;

/* ------------------------------------------------------------------ */
/* Inline SVG icons (stroke = currentColor)                            */
/* ------------------------------------------------------------------ */
const Icon = {
    // Both variants render; CSS shows whichever suits the active theme. Only
    // the visible one carries alt text so a screen reader announces the
    // brand once.
    brand: (
        <>
            <img className="mark-dark" src="/aqana-mark.png" alt="Aqana" />
            <img className="mark-light" src="/aqana-mark-light.png" alt="" aria-hidden="true" />
        </>
    ),
    alert: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" />
        </svg>
    ),
    alertSmall: (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" />
        </svg>
    ),
    eye: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    ),
    eyeOff: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17.94 17.94A10.5 10.5 0 0 1 12 19c-6.5 0-10-7-10-7a17.6 17.6 0 0 1 4.06-4.94M9.9 4.24A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17.7 17.7 0 0 1-2.16 3.19" />
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            <path d="M2 2l20 20" />
        </svg>
    ),
    arrow: (
        <svg className="plg-arrow" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
    ),
};

function Spinner() {
    return (
        <svg className="plg-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.2-8.56" />
        </svg>
    );
}

/* Per-field inline error, rendered BELOW its input (ui-ux error-placement). */
function FieldError({ id, children }) {
    if (!children) return null;
    return (
        <p className="plg-field-error" id={id}>
            {Icon.alertSmall}
            <span>{children}</span>
        </p>
    );
}

/* ------------------------------------------------------------------ */
/* Screen                                                              */
/* ------------------------------------------------------------------ */
export default function PortalLogin() {
    const { login, setSelectedPortal } = useAuth();
    const { t } = useLanguage();

    const [tenantId, setTenantId] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [touched, setTouched] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [authError, setAuthError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const requiredMsg = t('field_required', {}, 'This field is required');
    const values = { tenant: tenantId.trim(), username: username.trim(), password };
    // Blur-or-submit gating: a field only shows its "required" error once the
    // user has left it (onBlur) or tried to submit — never while first typing.
    const fieldError = (name) =>
        (touched[name] || submitAttempted) && !values[name] ? requiredMsg : '';

    const markTouched = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setAuthError('');
        setSubmitAttempted(true);
        if (!values.tenant || !values.username || !values.password) return;

        setSubmitting(true);
        try {
            const data = await login(values.username, values.password, values.tenant);
            if (!data) {
                // Defensive: login() throws on failure today, but a falsy
                // resolve must read as failure too — never a silent no-op.
                setAuthError(t('invalid_credentials', {}, 'Invalid credentials'));
                setSubmitting(false);
                return;
            }
            // Stamp the portal flavour (same as web LoginPage's manager path);
            // AuthContext has flipped isAuthenticated, so PortalAppShell now
            // re-renders into PortalShell — no navigation from here.
            if (setSelectedPortal) setSelectedPortal('manager');
        } catch (err) {
            setAuthError(err?.response?.data?.detail || t('invalid_credentials', {}, 'Invalid credentials'));
            setSubmitting(false);
        }
    };

    const tenantErr = fieldError('tenant');
    const usernameErr = fieldError('username');
    const passwordErr = fieldError('password');

    return (
        <LiquidGlassBackground intensity="full">
            <style>{CSS}</style>
            <div className="plg-wrap">
                <GlassCard veil className="plg-card">
                    <div className="plg-brand">
                        <div className="plg-brandmark">{Icon.brand}</div>
                        <span className="plg-wordmark">Aqana</span>
                    </div>

                    <p className="plg-eyebrow">{t('manager_portal', {}, 'Manager Portal')}</p>
                    <h1 className="plg-title">{t('portal_login_title', {}, 'Welcome back')}</h1>
                    <p className="plg-sub">{t('portal_login_sub', {}, 'Sign in to manage your business')}</p>

                    <form onSubmit={handleSubmit} noValidate>
                        <div className="plg-field">
                            <label className="plg-label" htmlFor="plg-tenant">{t('tenant_id', {}, 'Business ID')}</label>
                            <input
                                id="plg-tenant"
                                className={`plg-input${tenantErr ? ' plg-invalid' : ''}`}
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder={t('six_digit_id', {}, '6-digit ID')}
                                value={tenantId}
                                onChange={(e) => setTenantId(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                onBlur={() => markTouched('tenant')}
                                aria-invalid={!!tenantErr}
                                aria-describedby={tenantErr ? 'plg-tenant-error' : undefined}
                                autoComplete="off"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                disabled={submitting}
                                required
                            />
                            <FieldError id="plg-tenant-error">{tenantErr}</FieldError>
                        </div>

                        <div className="plg-field">
                            <label className="plg-label" htmlFor="plg-username">{t('username', {}, 'Username')}</label>
                            <input
                                id="plg-username"
                                className={`plg-input${usernameErr ? ' plg-invalid' : ''}`}
                                type="text"
                                placeholder={t('enter_username', {}, 'Enter username')}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                onBlur={() => markTouched('username')}
                                aria-invalid={!!usernameErr}
                                aria-describedby={usernameErr ? 'plg-username-error' : undefined}
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                disabled={submitting}
                                required
                            />
                            <FieldError id="plg-username-error">{usernameErr}</FieldError>
                        </div>

                        <div className="plg-field" style={{ marginBottom: 18 }}>
                            <label className="plg-label" htmlFor="plg-password">{t('password', {}, 'Password')}</label>
                            <div className="plg-pwd">
                                <input
                                    id="plg-password"
                                    className={`plg-input${passwordErr ? ' plg-invalid' : ''}`}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder={t('enter_password', {}, 'Enter password')}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onBlur={() => markTouched('password')}
                                    aria-invalid={!!passwordErr}
                                    aria-describedby={passwordErr ? 'plg-password-error' : undefined}
                                    autoComplete="current-password"
                                    disabled={submitting}
                                    required
                                />
                                <button
                                    type="button"
                                    className="plg-toggle"
                                    data-testid="plg-password-toggle"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-pressed={showPassword}
                                    aria-label={showPassword
                                        ? t('hide_password', {}, 'Hide password')
                                        : t('show_password', {}, 'Show password')}
                                    disabled={submitting}
                                >
                                    {showPassword ? Icon.eyeOff : Icon.eye}
                                </button>
                            </div>
                            <FieldError id="plg-password-error">{passwordErr}</FieldError>
                        </div>

                        {authError && (
                            <div className="plg-error" role="alert">
                                {Icon.alert}
                                <span>{authError}</span>
                            </div>
                        )}

                        <button className="plg-btn" type="submit" disabled={submitting} aria-busy={submitting}>
                            {submitting ? <Spinner /> : null}
                            {submitting ? t('signing_in', {}, 'Signing in…') : t('sign_in', {}, 'Sign In')}
                            {!submitting && Icon.arrow}
                        </button>
                    </form>

                    <div className="plg-foot">Aqana</div>
                </GlassCard>
            </div>
        </LiquidGlassBackground>
    );
}
